import React from 'react';
import styled, { css } from 'styled-components';
import { MobileAppFlair } from 'types/graphql';

type FlairWrapperProps = {
  flairType: MobileAppFlair;
  color: string;
  children: React.ReactNode;
};

const FLAIR_VIEWBOX = '0 0 100 30';

function getFlairPath(flairType: MobileAppFlair): string {
  switch (flairType) {
    case MobileAppFlair.angled:
      return 'M0 14 L100 0 L100 30 L0 30 Z';
    case MobileAppFlair.curved:
      return 'M0 20 Q50 0 100 20 L100 30 L0 30 Z';
    case MobileAppFlair.jagged:
      return 'M0 30 L0 12 L3 0 L6 12 L9 0 L13 12 L16 0 L19 12 L22 0 L25 12 L28 0 L31 12 L34 0 L38 12 L41 0 L44 12 L47 0 L50 12 L53 0 L56 12 L59 0 L63 12 L66 0 L69 12 L72 0 L75 12 L78 0 L81 12 L84 0 L88 12 L91 0 L94 12 L97 0 L100 12 L100 30 Z';
    case MobileAppFlair.wavy:
      return 'M0 15 C16 0 33 30 50 15 C66 0 83 30 100 15 L100 30 L0 30 Z';
    case MobileAppFlair.none:
      return '';
    default: {
      const _exhaustiveCheck: never = flairType;
      return _exhaustiveCheck;
    }
  }
}

/**
 * Wraps a mobile app grid section with a colored background and a matching flair
 * edge above and below the content.
 */
export function FlairWrapper({ flairType, color, children }: FlairWrapperProps): JSX.Element {
  const path = getFlairPath(flairType);

  if (flairType === MobileAppFlair.none || !path) {
    return <>{children}</>;
  }

  return (
    <Wrapper>
      <FlairEdge aria-hidden='true'>
        <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
          <path d={path} fill={color} />
        </svg>
      </FlairEdge>
      <Content $color={color}>{children}</Content>
      <FlairEdge aria-hidden='true' $flipped $rotated={flairType === MobileAppFlair.angled}>
        <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
          <path d={path} fill={color} />
        </svg>
      </FlairEdge>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;
`;

const Content = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
`;

const FlairEdge = styled.div<{ $flipped?: boolean; $rotated?: boolean }>`
  height: 24px;
  line-height: 0;
  width: 100%;

  ${({ $flipped }) =>
    $flipped &&
    css`
      transform: scaleY(-1);
    `}
  ${({ $flipped, $rotated }) =>
    $flipped &&
    $rotated &&
    css`
      transform: scaleX(-1) scaleY(-1);
    `}

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`;
